/**
 * useOnboardingEditorActionsV2 Hook
 *
 * V2 editor actions hook — manages version switching and editor actions
 * using V2 StepNode data from the screensV2 subcollection.
 *
 * V2 behavior:
 * - Uses StepNodeScreenService and the screensV2 subcollection
 * - Export format: FlowDocument (V2)
 * - Import format: FlowDocument (V2) only
 * - Version clone copies screensV2 documents directly
 *
 * Sprint: Schema V2 Migration - Sprint 1
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { StepNodeScreenService } from '@modules/firebase-core/services/StepNodeScreenService';
import type { OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import type { StepNode } from '@segmently/schema-models/v2';
import type {
  CliFunnelLocaleExportData,
  CliFunnelLocaleExportFormat,
  CliFunnelLocaleImportData,
  CliResponseEnvelope,
} from '@modules/shared-types';
import {
  buildOnboardingV2JsonDocument,
  formatFlowDocumentReferenceIssues,
  prepareOnboardingV2ImportData,
  writeImportedVersionGraph,
} from '@modules/onboardings/services/flowDocumentJsonV2';

const IMPORT_WARNING_SESSION_KEY = 'segmently:onboarding-v2-import-warning';

export interface UseOnboardingEditorActionsV2Props {
  projectId: string;
  onboardingId: string;
  initialVersionId: string;
  userId: string;
}

export interface UseOnboardingEditorActionsV2Return {
  // Version management
  versions: OnboardingVersion[];
  selectedVersion: OnboardingVersion | null;
  selectedVersionId: string;
  loadingVersions: boolean;
  switchVersion: (versionId: string) => void;
  /**
   * Create a new version as a full copy of the selected one. R7/P3 (no-dead-end):
   * `carryScreenPatch` swaps one screen's content for the given payload during the
   * copy — a screen-editor SAVE on a frozen version retargets here so no work is lost.
   */
  createVersion: (name: string, options?: { carryScreenPatch?: StepNode; description?: string }) => Promise<OnboardingVersion>;
  /** Freeze a just-published version (Spec A / D51, A4-1) — persists AND patches local state. */
  markVersionFrozen: (versionId: string) => Promise<void>;

  // Actions
  exportJSON: () => Promise<void>;
  importJSON: (file: File) => Promise<void>;
  exportLocales: (format: CliFunnelLocaleExportFormat) => Promise<void>;
  importLocales: (file: File) => Promise<void>;

  // State
  loading: boolean;
  error: string | null;
  clearError: () => void;
  successMessage: string | null;
  clearSuccess: () => void;
  warningMessage: string | null;
  clearWarning: () => void;
}

export function useOnboardingEditorActionsV2({
  projectId,
  onboardingId,
  initialVersionId,
  userId,
}: UseOnboardingEditorActionsV2Props): UseOnboardingEditorActionsV2Return {
  // Services
  const onboardingService = useMemo(() => new OnboardingService(), []);
  const stepNodeService = useMemo(() => new StepNodeScreenService(), []);

  // State
  const [versions, setVersions] = useState<OnboardingVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>(initialVersionId);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  useEffect(() => {
    try {
      const pendingWarning = window.sessionStorage.getItem(IMPORT_WARNING_SESSION_KEY);
      if (pendingWarning) {
        window.sessionStorage.removeItem(IMPORT_WARNING_SESSION_KEY);
        setWarningMessage(pendingWarning);
      }
    } catch {
      // Session storage can be unavailable in private/sandboxed contexts.
    }
  }, []);

  // Computed: selected version object
  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) || null,
    [versions, selectedVersionId]
  );

  // Load versions on mount
  useEffect(() => {
    async function loadVersions() {
      setLoadingVersions(true);
      try {
        const list = await onboardingService.listVersions(projectId, onboardingId);
        setVersions(list);

        if (list.length > 0) {
          if (initialVersionId && list.find((v) => v.id === initialVersionId)) {
            // The host mounts with initialVersionId='' while the version doc
            // loads, which used to race this effect into selecting list[0]
            // (head) and IGNORING the later-arriving explicit version (e.g.
            // the Reports `?version=` deep link). When the prop settles on a
            // real version, select exactly it.
            setSelectedVersionId(initialVersionId);
          } else if (!list.find((v) => v.id === initialVersionId)) {
            // No (valid) explicit version — default to the first version.
            setSelectedVersionId(list[0].id);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load versions');
      } finally {
        setLoadingVersions(false);
      }
    }

    if (projectId && onboardingId) {
      loadVersions();
    }
  }, [projectId, onboardingId, initialVersionId, onboardingService]);

  // Switch version
  const switchVersion = useCallback((versionId: string) => {
    setSelectedVersionId(versionId);
  }, []);

  // Freeze a just-published version (Spec A / D51, A4-1). The hook owns the
  // versions state, so patch it locally too — the editor's freeze-guard banner
  // must reflect the frozen flag immediately, without a remount.
  const markVersionFrozen = useCallback(
    async (versionId: string): Promise<void> => {
      const publishedAt = new Date().toISOString();
      await onboardingService.updateVersion(projectId, onboardingId, versionId, {
        frozen: true,
        publishedAt,
      });
      setVersions((prev) =>
        prev.map((v) => (v.id === versionId ? { ...v, frozen: true, publishedAt } : v))
      );
      // Re-sync from Firestore after the local patch: the publish flow writes
      // version data this hook cannot see (the deploy dialog saves analytics
      // privacy decisions onto version.variables) — stale state here would be
      // copied into the next branched version.
      try {
        const list = await onboardingService.listVersions(projectId, onboardingId);
        setVersions(list);
      } catch (err) {
        console.warn('[useOnboardingEditorActionsV2] Post-publish versions refresh failed:', err);
      }
    },
    [projectId, onboardingId, onboardingService]
  );

  // Create a new version from current V2 metadata and copy screensV2 directly.
  // R7/P3: carryScreenPatch swaps one screen for the pending editor payload during
  // the copy, BEFORE the selection switch — the remounted canvas reads it fresh.
  const createVersion = useCallback(
    async (name: string, options?: { carryScreenPatch?: StepNode; description?: string }): Promise<OnboardingVersion> => {
      setLoading(true);
      setError(null);
      try {
        if (!selectedVersion) {
          throw new Error('No version selected');
        }

        const v2Screens = await stepNodeService.listScreens(projectId, onboardingId, selectedVersionId);
        // Branch from a FRESH read of the source version, not the versions
        // state loaded at mount. Other surfaces write to the version doc
        // without going through this hook — the publish privacy review
        // (DeployWebPlacementDialog) saves excludeFromAnalytics decisions
        // straight onto version.variables — and copying the stale in-memory
        // object would mint the new version unreviewed, re-asking every
        // decision on the next publish.
        const freshSourceVersion = await onboardingService.getVersion(
          projectId,
          onboardingId,
          selectedVersionId
        );
        const sourceVersion = freshSourceVersion ?? selectedVersion;
        const sourceMetadata = sourceVersion.screensGraph;
        const newVersion = await onboardingService.createVersion(
          projectId,
          onboardingId,
          {
            ...sourceMetadata,
            screens: {},
            schemaVersion: sourceMetadata?.schemaVersion || '2.0.0',
          },
          name,
          userId,
          selectedVersionId
        );

        const patch = options?.carryScreenPatch;
        const patchKey = patch ? String((patch as { entityId?: string }).entityId ?? patch.id ?? '') : '';
        let patchApplied = false;
        for (const step of v2Screens) {
          const stepKey = String((step as { entityId?: string }).entityId ?? step.id ?? '');
          const usePatch = Boolean(patch) && patchKey !== '' && stepKey === patchKey;
          await stepNodeService.addScreen(projectId, onboardingId, newVersion.id, usePatch ? (patch as StepNode) : step);
          if (usePatch) patchApplied = true;
        }
        if (patch && !patchApplied) {
          // The edited screen was not in the source copy (defensive) — append it.
          await stepNodeService.addScreen(projectId, onboardingId, newVersion.id, patch);
        }

        const versionPatch: Record<string, unknown> = {};
        if (sourceVersion.variables) versionPatch.variables = sourceVersion.variables;
        // U8 (Spec D2): operator description rides the same post-create update.
        if (options?.description?.trim()) versionPatch.description = options.description.trim();
        if (Object.keys(versionPatch).length > 0) {
          await onboardingService.updateVersion(projectId, onboardingId, newVersion.id, versionPatch);
        }

        // Refresh versions list
        const updatedList = await onboardingService.listVersions(projectId, onboardingId);
        setVersions(updatedList);

        // Select new version
        setSelectedVersionId(newVersion.id);

        setSuccessMessage(patch
          ? `Saved to new version "${name}" — the published version is unchanged`
          : `Version "${name}" created successfully`);
        return newVersion;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create version';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, onboardingId, selectedVersionId, selectedVersion, userId, onboardingService, stepNodeService]
  );

  // Build V2 export data (FlowDocument format)
  const buildExportData = useCallback(async () => {
    if (!selectedVersion) {
      throw new Error('No version selected');
    }

    const screens = await stepNodeService.listScreens(projectId, onboardingId, selectedVersionId);

    return buildOnboardingV2JsonDocument({
      selectedVersion,
      selectedVersionId,
      projectId,
      onboardingId,
      screens,
    });
  }, [projectId, onboardingId, selectedVersionId, selectedVersion, stepNodeService]);

  // Export JSON (V2 format)
  const exportJSON = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const exportData = await buildExportData();

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const cleanVersionName = (selectedVersion?.name || 'version')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `onboarding_v2_${cleanVersionName}_${timestamp}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('V2 JSON exported successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export JSON';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [buildExportData, selectedVersion?.name]);

  // Import JSON (V2 FlowDocument format)
  const importJSON = useCallback(
    async (file: File): Promise<void> => {
      setLoading(true);
      setError(null);
      setWarningMessage(null);
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        const importData = prepareOnboardingV2ImportData(data);
        const { importedVariables, referenceIssues, referenceWarning, screens } = importData;

        if (referenceWarning) {
          console.warn(
            `Imported V2 JSON has unresolved condition references; import will continue: ${formatFlowDocumentReferenceIssues(referenceIssues)}`,
            referenceIssues,
          );
        }

        // Get current V2 screens to delete
        const currentScreens = await stepNodeService.listScreens(
          projectId,
          onboardingId,
          selectedVersionId
        );

        // Delete existing V2 screens
        for (const screen of currentScreens) {
          await stepNodeService.deleteScreen(projectId, onboardingId, selectedVersionId, screen.id);
        }

        // V2 FlowDocument — save StepNodes directly.
        for (const screenId of Object.keys(screens)) {
          const stepNode = screens[screenId];
          // Imported JSON is untrusted; the spread result is only structurally a StepNode.
          await stepNodeService.addScreen(projectId, onboardingId, selectedVersionId, {
            ...(stepNode as Record<string, unknown>),
            id: screenId,
          } as unknown as StepNode);
        }

        // Version-level data: screensGraph metadata, variables and the motion defaults.
        await writeImportedVersionGraph(
          onboardingService,
          { projectId, onboardingId, versionId: selectedVersionId },
          importData,
        );

        const updatedList = await onboardingService.listVersions(projectId, onboardingId);
        setVersions(updatedList);

        if (referenceWarning) {
          setWarningMessage(referenceWarning);
          try {
            window.sessionStorage.setItem(IMPORT_WARNING_SESSION_KEY, referenceWarning);
          } catch {
            // Best-effort notice for the post-import reload path.
          }
        } else {
          setSuccessMessage(
            `Imported ${Object.keys(screens).length} screens and ${Object.keys(importedVariables).length} variables successfully`
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import JSON';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, onboardingId, selectedVersionId, stepNodeService, onboardingService]
  );

  const exportLocales = useCallback(
    async (format: CliFunnelLocaleExportFormat): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ format });
        if (selectedVersion?.screensGraph?.defaultLanguage) {
          params.set('source', selectedVersion.screensGraph.defaultLanguage);
        }
        const languages = selectedVersion?.screensGraph?.languages;
        if (Array.isArray(languages) && languages.length > 0) {
          const source = selectedVersion?.screensGraph?.defaultLanguage || languages[0];
          let targets = languages.filter((locale) => locale !== source);
          if (targets.length === 0) {
            const promptedTargets = window.prompt(
              'Target locale(s) to add, comma-separated (for example: es-ES). Leave empty to export source-only.',
              ''
            );
            if (promptedTargets === null) {
              return;
            }
            targets = parseLocaleList(promptedTargets).filter((locale) => locale !== source);
          }
          if (targets.length > 0) {
            params.set('targets', targets.join(','));
          }
        }

        const response = await localeApiRequest<CliFunnelLocaleExportData>(
          projectId,
          `/funnels/${encodeURIComponent(onboardingId)}/versions/${encodeURIComponent(selectedVersionId)}/locales/export?${params.toString()}`,
        );
        downloadBase64File(response, response.filename);
        setSuccessMessage(`Locale ${format.toUpperCase()} exported successfully`);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to export locales';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, onboardingId, selectedVersionId, selectedVersion],
  );

  const importLocales = useCallback(
    async (file: File): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const contentBase64 = await fileToBase64(file);
        const response = await localeApiRequest<CliFunnelLocaleImportData>(
          projectId,
          `/funnels/${encodeURIComponent(onboardingId)}/versions/${encodeURIComponent(selectedVersionId)}/locales/import`,
          {
            method: 'POST',
            body: {
              format: 'xlsx',
              filename: file.name,
              contentBase64,
            },
          },
        );
        const updatedList = await onboardingService.listVersions(projectId, onboardingId);
        setVersions(updatedList);
        setSuccessMessage(
          `Imported ${response.summary.changedEntryCount} localized entries for ${response.summary.targetLocales.join(', ')}`
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import locales';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, onboardingId, selectedVersionId, onboardingService],
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear success
  const clearSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  const clearWarning = useCallback(() => {
    setWarningMessage(null);
  }, []);

  return {
    // Version management
    versions,
    selectedVersion,
    selectedVersionId,
    loadingVersions,
    switchVersion,
    createVersion,
    markVersionFrozen,

    // Actions
    exportJSON,
    importJSON,
    exportLocales,
    importLocales,

    // State
    loading,
    error,
    clearError,
    successMessage,
    clearSuccess,
    warningMessage,
    clearWarning,
  };
}

async function localeApiRequest<T>(
  projectId: string,
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown } = {},
): Promise<T> {
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error('You must be signed in to use locale export/import.');
  }

  const token = await user.getIdToken();
  const baseUrl = import.meta.env.VITE_BUILDER_API_URL || '';
  const requestUrl = `${baseUrl}/api/cli/v1/projects/${encodeURIComponent(projectId)}${path}`;
  let response: Response;
  try {
    response = await fetch(requestUrl, {
      method: options.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${token}`,
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'network request failed';
    throw new Error(
      `Could not reach Builder API for locale export/import at ${baseUrl || window.location.origin}. `
      + 'Check VITE_BUILDER_API_URL and make sure Builder Service is running. '
      + `Details: ${detail}`
    );
  }
  const payload = await response.json().catch(() => null) as CliResponseEnvelope<T> | { detail?: string; message?: string } | null;
  if (!response.ok) {
    throw new Error(
      String((payload && 'detail' in payload ? payload.detail : undefined)
        || (payload && 'message' in payload ? payload.message : undefined)
        || `Locale request failed with HTTP ${response.status}`)
    );
  }
  if (!payload || !('data' in payload)) {
    throw new Error('Locale request returned an invalid response.');
  }
  return payload.data;
}

function downloadBase64File(data: CliFunnelLocaleExportData, filename: string): void {
  const bytes = base64ToBytes(data.contentBase64);
  const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: data.contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.slice(index, index + chunkSize));
  }
  return window.btoa(binary);
}

function parseLocaleList(value: string): string[] {
  return Array.from(new Set(value
    .split(',')
    .map((locale) => locale.trim())
    .filter(Boolean)));
}

function base64ToBytes(value: string): Uint8Array {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
